import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpen, FileText, ChevronRight, FlaskConical, Brain, Timer, Shield, ExternalLink, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

interface Resource {
  title: string;
  description: string;
  url: string;
  type: "pdf" | "guide" | "poster";
}

interface ResourceCategory {
  id: string;
  name: string;
  nameKo: string;
  icon: React.ElementType;
  color: string;
  resources: Resource[];
}

const resourceCategories: ResourceCategory[] = [
  {
    id: "general",
    name: "General Resources",
    nameKo: "일반 자료",
    icon: BookOpen,
    color: "bg-jax-blue",
    resources: [
      {
        title: "마우스 명명법 가이드",
        description: "Quick Guide to Mouse Nomenclature - 마우스 명명 규칙에 대한 가이드",
        url: "/resources/poster-quick-guide-to-mouse-nomenclature.pdf",
        type: "poster",
      },
    ],
  },
  {
    id: "cancer",
    name: "Cancer Research",
    nameKo: "암 연구 모델",
    icon: FlaskConical,
    color: "bg-rose-500",
    resources: [
      {
        title: "암 연구 모델 가이드",
        description: "Cancer Research Model - 암 연구에 적합한 마우스 모델 안내",
        url: "/resources/Cancer_research_model.pdf",
        type: "guide",
      },
    ],
  },
  {
    id: "aging",
    name: "Aging Research",
    nameKo: "노화 연구 모델",
    icon: Timer,
    color: "bg-amber-500",
    resources: [
      {
        title: "노화 C57BL/6J 마우스 연구",
        description: "Aged C57BL/6J Mice for Research Studies - 노화 연구용 마우스 가이드",
        url: "/resources/aged-c57bl-6j-mice-for-research-studies.pdf",
        type: "guide",
      },
      {
        title: "노화 B6 연구 고려사항",
        description: "Aged B6 Study Considerations Whitepaper - 노화 마우스 연구 백서",
        url: "/resources/whitepaper-aged-b6-study-considerations.pdf",
        type: "guide",
      },
      {
        title: "노화 HET3 기술 자료",
        description: "JAX Aged HET3 Tech Sheet - HET3 노화 모델 기술 정보",
        url: "/resources/JAX-aged-het3-tech-sheet-digital.pdf",
        type: "guide",
      },
      {
        title: "노화 NASH 모델",
        description: "Aged NASH Models Tech Sheet - 비알코올성 지방간 노화 모델",
        url: "/resources/aged-nash-models-tech-sheet.pdf",
        type: "guide",
      },
    ],
  },
  {
    id: "immunodeficient",
    name: "Immunodeficient Models",
    nameKo: "면역결핍 모델",
    icon: Shield,
    color: "bg-emerald-500",
    resources: [
      {
        title: "면역결핍 모델 포스터",
        description: "JAX Immunodeficient Models Poster - 면역결핍 마우스 모델 개요",
        url: "/resources/jax-immunodeficient-models-poster.pdf",
        type: "poster",
      },
      {
        title: "NSG 변이체 개요",
        description: "NSG Variants One-Pager - NSG 마우스 변이체 비교",
        url: "/resources/onepager-nsg-variants.pdf",
        type: "guide",
      },
      {
        title: "신규 NSG 마우스 계통",
        description: "New Mouse Strains: NSG-SGM3xIL15xDKO & NSG-Flt3LxDKO",
        url: "/resources/poster-new-mouse-strains-nsg-sgm3xil15xdko-and-nsg-flt3lxdko.pdf",
        type: "poster",
      },
      {
        title: "PDX 모델 가이드",
        description: "Patient-Derived Xenograft Models - 환자 유래 이종이식 모델",
        url: "/resources/pdx-models.pdf",
        type: "guide",
      },
      {
        title: "NSG 면역 재구성 평가",
        description: "Human Immune System Reconstitution in NSG & NSG-SGM3",
        url: "/resources/comprehensive-evaluation-of-human-immune-system-reconstitution-in-nsg-and-nsg-sgm3-mouse-models.pdf",
        type: "guide",
      },
      {
        title: "사이토카인 방출 증후군 연구",
        description: "A New Way to Study Cytokine Release Syndrome",
        url: "/resources/a-new-way-to-study-cytokine-release-syndrome.pdf",
        type: "guide",
      },
      {
        title: "CAR-T 세포치료 CRS 연구",
        description: "CRS Studies for CAR-T Cell Therapy",
        url: "/resources/crs-studies-for-car-t-cell-therapy.pdf",
        type: "guide",
      },
      {
        title: "GVHD 효능 연구",
        description: "GVHD Efficacy Study - 이식편대숙주병 연구",
        url: "/resources/gvhd-efficacy-study-JAX.pdf",
        type: "guide",
      },
      {
        title: "Onco-Hu 연구",
        description: "Onco-Hu Studies - 인간화 종양 모델 연구",
        url: "/resources/onco-hu-studies.pdf",
        type: "guide",
      },
      {
        title: "감염병 연구 백서",
        description: "Infectious Disease Whitepaper - 감염병 연구 모델 가이드",
        url: "/resources/whitepaper-infectious-disease.pdf",
        type: "guide",
      },
    ],
  },
  {
    id: "neurodegenerative",
    name: "Neurodegenerative Diseases",
    nameKo: "신경퇴행성 질환 모델",
    icon: Brain,
    color: "bg-purple-500",
    resources: [
      {
        title: "알츠하이머병 개요",
        description: "Alzheimer's Disease One-Pager - 알츠하이머 연구 모델 요약",
        url: "/resources/onepager-alzheimers-disease.pdf",
        type: "guide",
      },
      {
        title: "알츠하이머 신규 모델",
        description: "Emerging Models for Alzheimer's Disease - 최신 알츠하이머 연구 모델",
        url: "/resources/emerging-models-for-alzheimers-disease.pdf",
        type: "guide",
      },
      {
        title: "파킨슨병 마우스 포트폴리오",
        description: "Mice for Parkinson's Disease - 파킨슨 연구용 마우스 모델",
        url: "/resources/portfolio-mice-for-parkinsons.pdf",
        type: "guide",
      },
      {
        title: "ALS 마우스 연구 가이드",
        description: "Working with ALS Mice 2024 - 근위축성측삭경화증 연구 가이드",
        url: "/resources/working-with-als-mice-2024.pdf",
        type: "guide",
      },
      {
        title: "신경생물학 핸드북",
        description: "Neurobiology Booklet - 신경생물학 연구를 위한 종합 안내서",
        url: "/resources/neurobiology-booklet.pdf",
        type: "guide",
      },
    ],
  },
];

interface ResourceWithCategory extends Resource {
  categoryId: string;
  categoryName: string;
  categoryColor: string;
  CategoryIcon: React.ElementType;
}

const ResourceCard = ({ resource }: { resource: ResourceWithCategory }) => {
  const fileName = resource.url.split('/').pop();
  
  return (
    <Link
      to={`/view-resource?file=${fileName}`}
      target="_blank"
      className="group flex flex-col bg-white rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300"
    >
      {/* PDF Preview using iframe */}
      <div className="relative aspect-[4/3] bg-white overflow-hidden rounded-t-lg">
        <div className="absolute inset-[-2px] overflow-hidden">
          <iframe
            src={`${resource.url}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
            className="w-[calc(200%+8px)] h-[calc(200%+8px)] origin-top-left scale-50 pointer-events-none border-0 -ml-1 -mt-1"
            title={resource.title}
            style={{ border: 'none' }}
          />
        </div>
        <div className="absolute inset-0 bg-transparent group-hover:bg-black/5 transition-colors" />
        {/* External Link Icon */}
        <div className="absolute bottom-2 right-2 w-8 h-8 bg-jax-blue/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
          <ExternalLink className="w-4 h-4 text-white" />
        </div>
      </div>
      
      {/* Card Content */}
      <div className="p-3 bg-white">
        <h4 className="text-sm font-medium text-foreground group-hover:text-jax-blue transition-colors line-clamp-2">
          {resource.title}
        </h4>
      </div>
    </Link>
  );
};

const Resources = () => {
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  
  // Flatten all resources with category info
  const allResources: ResourceWithCategory[] = resourceCategories.flatMap(category => 
    category.resources.map(resource => ({
      ...resource,
      categoryId: category.id,
      categoryName: category.nameKo,
      categoryColor: category.color,
      CategoryIcon: category.icon,
    }))
  );

  // Filter resources based on active filter
  const filteredResources = activeFilter 
    ? allResources.filter(r => r.categoryId === activeFilter)
    : allResources;

  // Handle hash-based navigation
  useEffect(() => {
    if (location.hash) {
      const categoryId = location.hash.replace('#resource-', '');
      if (resourceCategories.some(cat => cat.id === categoryId)) {
        setActiveFilter(categoryId);
      }
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead 
        title="연구 자료실 - Central TG"
        description="JAX® 동물 모델에 대한 다양한 연구 자료와 가이드를 다운로드하세요. 암 연구, 노화, 면역결핍, 신경퇴행성 질환 모델 관련 자료."
        keywords="JAX 연구자료, 마우스 모델 가이드, 암 연구 모델, 면역결핍 모델, 신경퇴행성 질환"
        canonical="https://centraltg.co.kr/resources"
      />
      <Header />
      
      <main className="flex-1">
        <section className="py-20 bg-gradient-to-b from-background to-muted/30">
          <div className="container max-w-7xl mx-auto container-padding">
            <div className="text-center mb-8">
              <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                연구자를 위한 자료
              </h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                JAX® 동물 모델에 대한 다양한 연구 자료와 가이드를 다운로드하세요.
              </p>
            </div>

            {/* Category Filter - Mobile Dropdown */}
            <div className="md:hidden mb-6">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between px-4 py-3 bg-white border border-border rounded-lg text-sm font-medium">
                    <span className="flex items-center gap-2">
                      {activeFilter ? (
                        <>
                          {(() => {
                            const cat = resourceCategories.find(c => c.id === activeFilter);
                            if (cat) {
                              const Icon = cat.icon;
                              return <Icon className="w-4 h-4" />;
                            }
                            return null;
                          })()}
                          {resourceCategories.find(c => c.id === activeFilter)?.nameKo}
                        </>
                      ) : (
                        <>전체 ({allResources.length})</>
                      )}
                    </span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[calc(100vw-2rem)] bg-white z-50" align="center">
                  <DropdownMenuItem 
                    onClick={() => setActiveFilter(null)}
                    className={activeFilter === null ? 'bg-jax-blue/10 text-jax-blue' : ''}
                  >
                    전체 ({allResources.length})
                  </DropdownMenuItem>
                  {resourceCategories.filter(c => c.resources.length > 0).map(category => {
                    const IconComponent = category.icon;
                    return (
                      <DropdownMenuItem
                        key={category.id}
                        onClick={() => setActiveFilter(category.id)}
                        className={activeFilter === category.id ? 'bg-jax-blue/10 text-jax-blue' : ''}
                      >
                        <IconComponent className="w-4 h-4 mr-2" />
                        {category.nameKo} ({category.resources.length})
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Category Filter - Desktop Buttons */}
            <div className="hidden md:flex flex-wrap justify-center gap-2 mb-8">
              <button
                onClick={() => setActiveFilter(null)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === null 
                    ? 'bg-jax-blue text-white' 
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                전체 ({allResources.length})
              </button>
              {resourceCategories.filter(c => c.resources.length > 0).map(category => {
                const IconComponent = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveFilter(category.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                      activeFilter === category.id 
                        ? `${category.color} text-white` 
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <IconComponent className="w-3.5 h-3.5" />
                    {category.nameKo} ({category.resources.length})
                  </button>
                );
              })}
            </div>

            {/* Unified Resource Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {filteredResources.map((resource, idx) => (
                <ResourceCard key={idx} resource={resource} />
              ))}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Resources;
